import { useLang } from '@/i18n';
import { useEffect, useState, useRef } from 'react';
import css from './index.module.scss';
import EcoVideo from 'eco-design/base/eco-video';
import EcoImg from 'eco-design/base/eco-img';
import { useWebpOrJpg } from '../../hooks/useWebpOrJpg';
import getLocal from '@/app/utils/i18n';

const B: React.FC = () => {
    const { t } = useLang('dpux');
    const { locale } = getLocal();
    const webpOrJpg = useWebpOrJpg('#delta-pro-ultra-x-B');
    const [loadImg, setLoadImg] = useState<boolean>(false);
    const [infoEnter, setInfoEnter] = useState<boolean>(false);
    const [textEnter, setTextEnter] = useState<boolean>(false);
    const imgMap: Record<string, HTMLImageElement> = {};
    const boxRef = useRef<HTMLElement>(null);
    const splitTimesA = 10; //滚动触发的间隔;毫秒
    const throttlePlayingRefA = useRef<number>(Date.now()); //用于少点移动
    const labelLetters = t('B_label')?.replaceAll(' ', '\u00A0')?.split('');
    const titleLetters = t('B_title')?.replaceAll(' ', '\u00A0')?.split('');
    const letterArr = [...labelLetters, ...titleLetters];
    const FROMES_NUM = 138;

    const checkWebPSupport = (callback: (isWebPSupported: boolean) => void) => {
        const webP = new Image();
        // 这是一个 内联的 Base64 编码的 WebP 图片，无需额外网络请求。
        // 图片尺寸仅为 2x1 像素（极小，仅用于检测格式支持）。
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
        webP.onload = webP.onerror = () => {
            callback(webP.height === 2);
        };
    };
    const loadImages = async (useWebP: boolean) => {
        for (let i = 0; i <= FROMES_NUM; i++) {
            const type = useWebP ? 'webp' : 'jpg';
            const frameNum = String(i + 3).padStart(4, '000');
            const url = `https://websiteoss.ecoflow.com/upload/sequence-frame/dpux-2/${type}/${i + 3}.${type}`;
            loadSingleImage(url, frameNum);
        }
    };

    const loadSingleImage = async (src: string, frameNum: string) => {
        return new Promise<HTMLImageElement>((resolve) => {
            const img = new Image();
            img.src = src;
            img.width = window.innerWidth;
            img.height = window.innerHeight;
            img.onload = () => resolve(img);
            imgMap[frameNum] = img;
        });
    };

    const renderCanvasImg = (curImg: HTMLImageElement) => {
        const canvas = document.getElementById('video-canvas-B') as HTMLCanvasElement;
        if (!canvas || !curImg) return;

        // 1. 获取逻辑尺寸（CSS像素）和物理尺寸
        const dpr = window.devicePixelRatio || 1;
        const logicalWidth = canvas.clientWidth;
        const logicalHeight = canvas.clientHeight;

        // 2. 设置Canvas物理分辨率（与CSS尺寸分离）
        canvas.width = Math.round(logicalWidth * dpr);
        canvas.height = Math.round(logicalHeight * dpr);

        const ctx = canvas.getContext('2d')!;

        // 3. 重置变换矩阵（重要！）
        ctx.resetTransform();

        // 4. 计算cover模式的绘制参数（基于逻辑尺寸）
        const imgRatio = curImg.naturalWidth / curImg.naturalHeight;
        const canvasRatio = logicalWidth / logicalHeight;

        let drawWidth, drawHeight, offsetX, offsetY;

        if (imgRatio > canvasRatio) {
            // 宽图：按高度裁切
            drawHeight = logicalHeight;
            drawWidth = drawHeight * imgRatio;
            offsetX = (logicalWidth - drawWidth) / 2;
            offsetY = 0;
        } else {
            // 高图：按宽度裁切
            drawWidth = logicalWidth;
            drawHeight = drawWidth / imgRatio;
            offsetX = 0;
            offsetY = (logicalHeight - drawHeight) / 2;
        }

        // 5. 清除画布（使用物理尺寸）
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // 6. 绘制到物理坐标系（自动适配DPI）
        ctx.drawImage(
            curImg,
            0,
            0,
            curImg.naturalWidth,
            curImg.naturalHeight, // 源图完整尺寸
            offsetX * dpr,
            offsetY * dpr, // 物理坐标
            drawWidth * dpr,
            drawHeight * dpr, // 物理尺寸
        );
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.innerWidth < 1024) return;
        if (loadImg) {
            checkWebPSupport((isWebPSupported) => {
                loadImages(isWebPSupported);
            });
            setTimeout(() => {
                renderCanvasImg(imgMap['0003']);
                const handleScroll = () => {
                    const nowTimesA = Date.now();
                    const nextTimeA = throttlePlayingRefA.current + splitTimesA;
                    if (nowTimesA < nextTimeA) {
                        // 现在的时间小于(上次的时间加个时间间隔)
                        return;
                    }
                    throttlePlayingRefA.current = nowTimesA;

                    const scrollY = window.scrollY;
                    const canvas = document.getElementById('video-canvas-B') as HTMLCanvasElement;
                    const section = boxRef.current;
                    if (!canvas || !section) return;

                    // 计算动画播放区间（从元素进入视口到完全离开）
                    const playHeight = window.innerHeight * 2;
                    const startScroll = section.offsetTop;
                    const endScroll = startScroll + playHeight;
                    // 如果滚动位置在动画区间内
                    if (scrollY >= startScroll && scrollY <= endScroll) {
                        // 计算当前帧索引
                        const progress = (scrollY - startScroll) / playHeight;
                        const frameIndex = String(Math.min(Math.floor(progress * FROMES_NUM), FROMES_NUM - 1)).padStart(4, '000');
                        if (imgMap[frameIndex + '']) {
                            renderCanvasImg(imgMap[frameIndex + '']);
                        }
                    }
                };
                window.addEventListener('scroll', handleScroll);
                return () => {
                    window.removeEventListener('scroll', handleScroll);
                };
            }, 1000);
        }
    }, [loadImg]);

    const canvas = useRef<HTMLCanvasElement>(null);
    const [canvasOpacity, setCanvasOpacity] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (window.innerWidth < 1024) return;
            const sceneB1 = AniContainer.create({
                triggerElement: `.delta-pro-ultra-x-A`,
                triggerHook: 1,
                offset: 100,
                onEnter: () => setLoadImg(true),
            });

            const sceneB2 = AniContainer.create({
                triggerElement: `.delta-pro-ultra-x-B`,
                triggerHook: 1,
                offset: '150%',
                duration: '150%',
                onEnter: () => setInfoEnter(true),
                onLeave: () => setInfoEnter(false),
            });

            const sceneB3 = AniContainer.create({
                triggerElement: `.delta-pro-ultra-x-B`,
                triggerHook: 1,
                offset: '140%',
                duration: '10%',
                syncFromTo: [['.delta-pro-ultra-x-B .mask', { opacity: 0 }, { opacity: 1 }]],
            });

            const split = 0.6 / letterArr?.length;
            let sceneLetterArr: any = [];
            letterArr?.map((_: string, k: number) => {
                sceneLetterArr[k] = AniContainer.create({
                    triggerElement: `.delta-pro-ultra-x-B`,
                    triggerHook: 1,
                    offset: `${(1.7 + k * split) * 100}%`,
                    duration: `${split * 10 * 100}%`,
                    syncFromTo: [[`.delta-pro-ultra-x-B .letter-${k}`, { opacity: 0.2 }, { opacity: 1 }]],
                    onProcess:
                        k === 12
                            ? (process: number) => {
                                  if (process <= 0) {
                                      setCanvasOpacity(false);
                                  }
                              }
                            : k === 67
                            ? (process: number) => {
                                  if (process >= 1) {
                                      setCanvasOpacity(false);
                                  } else if (process > 0 && process <= 0.1) {
                                      setCanvasOpacity(true);
                                  }
                              }
                            : undefined,
                    onEnter:
                        k === 12
                            ? () => {
                                  setCanvasOpacity(true);
                              }
                            : undefined,
                });
            });

            const sceneB4 = AniContainer.create({
                triggerElement: `.delta-pro-ultra-x-B`,
                triggerHook: 1,
                offset: '240%',
                duration: '10%',
                syncFromTo: [['.delta-pro-ultra-x-B .inner', { opacity: 1, y: 0 }, { opacity: 0, y: -120 }]],
            });

            const sceneB5 = AniContainer.create({
                triggerElement: `.delta-pro-ultra-x-B`,
                triggerHook: 1,
                offset: '240%',
                duration: '10%',
                syncFromTo: [['.delta-pro-ultra-x-B .cover', { opacity: 1 }, { opacity: 0 }]],
            });

            const sceneB6 = AniContainer.create({
                triggerElement: `.delta-pro-ultra-x-B`,
                triggerHook: 1,
                offset: '260%',
                duration: '140%',
                onEnter: () => setTextEnter(true),
                onLeave: () => setTextEnter(false),
            });

            return () => {
                const scenes = [sceneB1, sceneB2, sceneB3, sceneB4, sceneB5, sceneB6, ...sceneLetterArr];
                scenes?.map((item: any) => item.destroy());
            };
        }
    }, []);

    const screen1Ref = useRef<HTMLDivElement>(null);
    const screen2Ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.innerWidth >= 1024) return;
        if (!screen1Ref.current || !screen2Ref.current) return;
        const label = screen1Ref.current.querySelector('.m-label');
        const title = screen1Ref.current.querySelector('.m-title');
        const ani1 = AniContainer.create({
            triggerElement: screen1Ref.current,
            triggerHook: 1,
            offset: '20%',
            duration: '100%',
            onProcess: (process: number) => {
                if (process <= 0) {
                    label?.classList.remove('enter');
                    title?.classList.remove('enter');
                } else {
                    label?.classList.add('enter');
                    title?.classList.add('enter');
                }
            },
        });
        const text = screen2Ref.current.querySelector('.m-text');
        const ani2 = AniContainer.create({
            triggerElement: screen2Ref.current,
            triggerHook: 1,
            offset: '20%',
            duration: '100%',
            onEnter: () => {
                text?.classList.add('enter');
            },
            onProcess: (process: number) => {
                if (process <= 0) {
                    text?.classList.remove('enter');
                }
            },
        });
        return () => {
            ani1.destroy?.();
            ani2.destroy?.();
        };
    }, []);

    return (
        <section
            className={`${css.section} delta-pro-ultra-x-B GtmModuleData-section`}
            id="delta-pro-ultra-x-B"
            data-section-id={'delta-pro-ultra-x-B'}
            data-section-title={t('B_title')}
            ref={boxRef}
        >
            <div className="sticky pc">
                <canvas className={`video canvas ${canvasOpacity && infoEnter ? 'active' : ''}`} id="video-canvas-B" ref={canvas}></canvas>
                <div className="cover">
                    <div className="mask"></div>
                </div>
                <div className={`info${infoEnter ? ' enter' : ''}`}>
                    <div className="inner">
                        <ul className="label">
                            {labelLetters?.map((letter: string, k: number) => (
                                <li key={`label-${k}`} className={`letter letter-${k}`}>
                                    {letter}
                                </li>
                            ))}
                        </ul>
                        <ul className="title">
                            {titleLetters?.map((letter: string, k: number) => (
                                <li key={`title-${k}`} className={`letter letter-${k + labelLetters?.length}`}>
                                    {letter}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="text">
                    <div className={`text-ani ${textEnter ? 'enter' : ''}`}>
                        {t.rich('B_desc', {
                            h: (v: string) => <span className="h">{v}</span>,
                            s: (v: string) => <span className="s">{v}</span>,
                        })}
                    </div>
                </div>

                {locale === 'ca' && <div className="footer-tips mt">{t('item_Remarks3')}</div>}
            </div>

            {/* M */}
            <div className="sticky-m m">
                <div className="screen1-container">
                    <div className="screen1" ref={screen1Ref}>
                        <EcoVideo
                            pc="https://websiteoss.ecoflow.com/upload/video/1759030416636/B_M.mp4"
                            poster="https://websiteoss.ecoflow.com/upload/image/1760170012591/b.jpg"
                            autoPlay
                            loop
                        />
                        <div className="m-info">
                            <div className="m-label ani-1">{t('B_label')}</div>
                            <div className="m-title ani-2">{t('B_title')}</div>
                        </div>
                    </div>
                </div>
                <div className="screen2" ref={screen2Ref}>
                    <EcoImg
                        pc={webpOrJpg(
                            'https://websiteoss.ecoflow.com/upload/image/1760078893306/B_m.jpg',
                            'https://websiteoss.ecoflow.com/upload/image/1760078892907/B_m.webp',
                        )}
                    />
                    <div className="m-text ani-1">
                        {t.rich('B_desc', {
                            h: (v: string) => <span>{v}</span>,
                            sup: (v: string) => <sup>{v}</sup>,
                            s: (v: string) => <span className="s">{v}</span>,
                        })}
                    </div>
                </div>
                {locale === 'ca' && <div className="footer-tips mt">{t('item_Remarks3')}</div>}
            </div>
        </section>
    );
};

export default B;
